import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { setAuthToken, formatApiError } from "./api";
import { trackEvent } from "./analytics";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(undefined); // undefined = loading
    const [error, setError] = useState(null);

    const fetchMe = useCallback(async () => {
        // Skip the round-trip entirely when we have no token — otherwise the
        // landing/login pages generate noisy 401s in the browser console.
        let token = null;
        try { token = window.localStorage.getItem("lets_token"); } catch (_e) { /* ignore */ }
        if (!token) { setUser(null); return; }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch (e) {
            // Invalid / expired token — clear it so subsequent requests stop hitting 401.
            if (e?.response?.status === 401) setAuthToken(null);
            setUser(null);
        }
    }, []);

    useEffect(() => {
        fetchMe();
    }, [fetchMe]);

    const login = async (email, password) => {
        setError(null);
        try {
            const { data } = await api.post("/auth/login", { email, password });
            if (data.access_token) setAuthToken(data.access_token);
            setUser(data.user);
            trackEvent("login", { role: data.user?.role });
            return data.user;
        } catch (e) {
            const msg = formatApiError(e.response?.data?.detail) || e.message;
            setError(msg);
            throw new Error(msg);
        }
    };

    const register = async (payload) => {
        setError(null);
        try {
            const { data } = await api.post("/auth/register", payload);
            if (data.access_token) setAuthToken(data.access_token);
            setUser(data.user);
            trackEvent("signup", { role: data.user?.role });
            return data.user;
        } catch (e) {
            const msg = formatApiError(e.response?.data?.detail) || e.message;
            setError(msg);
            throw new Error(msg);
        }
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch (e) {
            /* ignore */
        }
        setAuthToken(null);
        setUser(null);
    };

    const refreshMe = fetchMe;

    return (
        <AuthContext.Provider
            value={{ user, error, login, register, logout, refreshMe }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}

export function roleHome(role) {
    if (!role) return "/login";
    if (role === "customer") return "/customer";
    if (role === "technician" || role === "supervisor_technician")
        return "/technician";
    return "/admin";
}
